import React, { useCallback, useState, ChangeEvent, useEffect, useRef } from 'react'
import Slider from 'rc-slider'
import styled, { css } from 'styled-components'
import Input from '@components/Input'
import { formatValue, formattedValueToNumber } from '@components/InputSlider/helpers'

export interface InputSlider {
  value?: number
  min?: number
  max?: number
  step?: number
  steps?: number[]
  onChange?: (value: number) => void
  className?: string
  disabled?: boolean
  label?: string
  aero?: boolean
  formatType?: FormatType
}

const SSlider = styled(Slider)<{ aero?: number }>`
  .rc-slider-handle {
    width: 20px;
    height: 20px;
    margin-top: -8px;
  }
  .rc-slider-rail,
  .rc-slider-track {
    border-radius: ${({
      theme: {
        general: { borderRadius },
      },
    }) => `0 0 ${borderRadius} ${borderRadius}`};
  }
  .rc-slider-track {
    background: ${({ theme, aero }) => (aero ? theme.gradients.aero : theme.gradients.main)};
  }

  .rc-slider-handle {
    border: 5px solid white;
    background: ${({ theme, aero }) => (aero ? theme.colors.darkBlue : theme.colors.green)};
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
    z-index: 1;
  }

  &.rc-slider-disabled {
    background: none;
    .rc-slider-track {
      background: ${({ theme }) => theme.colors.middleGray};
    }
    .rc-slider-handle {
      background: ${({ theme }) => theme.colors.middleGray};
    }
  }
`

const SLimiter = styled.div`
  position: absolute;
  bottom: -18px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.darkGray};
`
const SLimiterMin = styled(SLimiter)`
  left: 0;
`
const SLimiterMax = styled(SLimiter)`
  right: 0;
`

const SWrap = styled(Input)<{ disabled?: boolean }>`
  display: flex;
  position: relative;
  justify-content: space-between;

  ${({ disabled }) =>
    disabled &&
    css`
      background: ${({ theme }) => theme.colors.lightGray};
      color: ${({ theme }) => theme.colors.middleGray};
      border-color: ${({ theme }) => theme.colors.middleGray};
    `}

  ${SSlider} {
    position: absolute;
    width: calc(100% + 2px);
    bottom: -6px;
    left: -1px;
    right: -1px;
  }
`

const SInput = styled.input`
  background: none;
  border: none;
  flex-shrink: 0;
  outline: none;
  text-align: right;
  display: block;
  width: 36%;
`

const SLabel = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`

export enum FormatType {
  money,
  years,
}

const InputSlider: React.FC<InputSlider> = (props) => {
  const { value, min, max, step, steps, onChange, className, disabled, label, aero, formatType } = props
  const isChangeRef = useRef<boolean | null>(null)

  const [inputValue, setInputValue] = useState<string>(value ? `${formatValue(value, formatType)}` : '')
  const [sliderValue, setSliderValue] = useState(
    steps ? steps.findIndex((stepValue) => stepValue === value) + 1 : value ?? 0,
  )

  const startValue = steps?.[0] ?? min
  const endValue = steps?.[steps.length - 1] ?? max

  const validate = useCallback(
    (value: number) => {
      if (startValue && value < startValue) {
        return startValue
      } else if (endValue && value > endValue) {
        return endValue
      }

      return value
    },
    [startValue, endValue],
  )

  const onSliderChange = useCallback(
    (sliderValue: number) => {
      const newInputValue = `${formatValue(steps ? steps[sliderValue - 1] : sliderValue, formatType)}`

      setInputValue(newInputValue)

      setSliderValue(sliderValue)

      onChange?.(steps ? steps[sliderValue - 1] : sliderValue)
    },
    [steps, formatType],
  )

  const getValues = useCallback(
    (value: string) => {
      const formattedValue = validate(formattedValueToNumber(value))
      const sliderValue = steps ? steps.findIndex((stepValue) => stepValue === formattedValue) + 1 : formattedValue

      return { formattedValue, sliderValue }
    },
    [steps, validate],
  )

  const onInputBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value

      const { sliderValue, formattedValue } = getValues(value)

      setInputValue(`${formatValue(formattedValue, formatType)}`)
      setSliderValue(sliderValue)

      onChange?.(formattedValue)
    },
    [getValues],
  )

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value

      const { sliderValue } = getValues(value)

      setInputValue(value)
      setSliderValue(sliderValue)
    },
    [getValues],
  )

  useEffect(() => {
    const value = formattedValueToNumber(inputValue)

    if (steps) {
      const elementIndex = steps.findIndex((step) => step === value)

      if (elementIndex === -1) return

      setInputValue(`${formatValue(steps[elementIndex], formatType)}`)
      setSliderValue(elementIndex + 1)

      isChangeRef.current = true
    } else if (isChangeRef.current && min && value < min) {
      setInputValue(`${formatValue(min, formatType)}`)
      setSliderValue(min)

      onChange?.(min)
    }
  }, [steps, inputValue, min, onChange, formatType])

  return (
    <SWrap as="label" className={className} disabled={disabled}>
      <SLabel>{label}</SLabel>
      <SInput value={inputValue} disabled={disabled} onChange={onInputChange} onBlur={onInputBlur} />

      <SSlider
        {...{
          value: sliderValue,
          min,
          max,
          step,
          onChange: onSliderChange,
          disabled,
        }}
        aero={aero ? +aero : 0}
      />
      {startValue && <SLimiterMin>{startValue}</SLimiterMin>}

      {endValue && <SLimiterMax>{endValue}</SLimiterMax>}
    </SWrap>
  )
}

export default InputSlider
